import { useEffect, useContext, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import ManageSubscriptionModal from "./ManageSubscriptionModal";
import "./ProWelcome.css";

const PRICING = {
  monthly: {
    inr: 49, display: "₹49", per: "/ month", note: "Billed monthly · cancel anytime",
    strikethrough: null, effectivePer: null,
    globalDisplay: "$2", globalPer: "/ month", globalNote: "Billed monthly · cancel anytime",
    globalStrikethrough: null, globalEffectivePer: null,
    promoInr: "₹25", promoGlobal: "$1",
  },
  yearly: {
    inr: 399, display: "₹399", per: "/ year", note: "Billed once yearly · saves ₹189",
    strikethrough: "₹49/month", effectivePer: "₹33/mo effective",
    globalDisplay: "$16", globalPer: "/ year", globalNote: "Billed once yearly · saves ~$8",
    globalStrikethrough: "$2/month", globalEffectivePer: "$1.33/mo effective",
    promoInr: "₹200", promoGlobal: "$8",
  },
};

const MONTHLY_INR = PRICING.monthly.inr;
const YEARLY_INR = PRICING.yearly.inr;
const SAVE_PCT = Math.round((1 - (YEARLY_INR / 12) / MONTHLY_INR) * 100);
const PROMO_CODE = process.env.REACT_APP_PROMO_CODE || "LAUNCH50";
const PROMO_TOTAL_SLOTS = 50;

const GLOBAL_PRICES = [
  { flag: "🇺🇸", country: "United States", currency: "USD", monthly: "$2", yearly: "$16" },
  { flag: "🇬🇧", country: "United Kingdom", currency: "GBP", monthly: "£1.50", yearly: "£12" },
  { flag: "🇪🇺", country: "Europe", currency: "EUR", monthly: "€2", yearly: "€16" },
  { flag: "🇦🇺", country: "Australia", currency: "AUD", monthly: "A$3", yearly: "A$24" },
  { flag: "🇨🇦", country: "Canada", currency: "CAD", monthly: "C$3", yearly: "C$22" },
  { flag: "🇸🇬", country: "Singapore", currency: "SGD", monthly: "S$3", yearly: "S$22" },
  { flag: "🇦🇪", country: "UAE", currency: "AED", monthly: "AED 7", yearly: "AED 55" },
  { flag: "🇲🇾", country: "Malaysia", currency: "MYR", monthly: "RM 9", yearly: "RM 70" },
  { flag: "🇧🇷", country: "Brazil", currency: "BRL", monthly: "R$10", yearly: "R$80" },
  { flag: "🇵🇭", country: "Philippines", currency: "PHP", monthly: "₱110", yearly: "₱850" },
];

const FEATURES_FREE = [
  { text: "YouTube → structured course", yes: true },
  { text: "Progress tracking & analytics", yes: true },
  { text: "Public profile page", yes: true },
  { text: "Phase 1 of all tracks — free", yes: true },
  { text: "All track phases(only 1 phase)", yes: false },
  { text: "AI phase assessments", yes: false },
  { text: "Real-world projects", yes: false },
  { text: "Verified certificates", yes: false },
];

const FEATURES_PRO = [
  "All track phases unlocked",
  "Frontend, Backend, VLSI, Embedded tracks",
  "AI assessments — unlimited retakes",
  "Real-world projects per phase",
  "Verified + shareable certificates",
  "PRO badge on recruiter profile",
  "Readiness score & advanced analytics",
  "All future tracks at no extra cost",
];

const TESTIMONIALS = [
  {
    text: "Got my internship offer 2 months after completing the Full Stack track. The verified certificate actually came up in the interview.",
    initials: "AK", color: "blue",
    meta: "Arun K. · 3rd year, NIT Trichy · ₹18k stipend internship",
  },
  {
    text: "The AI assessments are what got me. I realized I didn't understand async JS until I failed three times. That forced me to fix the gap before placements.",
    initials: "PL", color: "teal",
    meta: "Priya L. · 2nd year, SRM · placed at Zoho",
  },
];

export default function Upgrade() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [billingCycle, setBillingCycle] = useState("monthly");
  const [billingConfig, setBillingConfig] = useState({ preferredGateway: "razorpay", isIndia: true });
  // FIX #1/#7: subscription is extracted directly from /billing/config response
  // No separate /billing/subscription endpoint needed
  const [subscription, setSubscription] = useState(null);
  const [paddleLoaded, setPaddleLoaded] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [paddleError, setPaddleError] = useState(null);
  const [status, setStatus] = useState(null);
  const [promoUsed, setPromoUsed] = useState(null);
  // eslint-disable-next-line
  const [promoCopied, setPromoCopied] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isPro =
    user?.subscription?.plan === "PRO" &&
    (user?.subscription?.status === "ACTIVE" ||
      (user?.subscription?.currentPeriodEnd &&
        new Date(user.subscription.currentPeriodEnd) > new Date()));

  const isIndia = billingConfig.isIndia !== false;
  const gateway = billingConfig.preferredGateway || "razorpay";
  const price = PRICING[billingCycle];

  const displayPrice = isIndia ? price.display : price.globalDisplay;
  const displayPer = isIndia ? price.per : price.globalPer;
  const displayNote = isIndia ? price.note : price.globalNote;
  const displayStrike = isIndia ? price.strikethrough : price.globalStrikethrough;
  const displayEffPer = isIndia ? price.effectivePer : price.globalEffectivePer;
  const displayFree = isIndia ? "₹0" : "$0";
  // eslint-disable-next-line
  const promoPrice = isIndia ? price.promoInr : price.promoGlobal;
  const gatewayName = gateway === "razorpay" ? "Razorpay" : "Paddle";

  const promoRemaining = promoUsed !== null
    ? (promoUsed < 45 ? (50 - promoUsed) : Math.max(3, 8 - (promoUsed % 6)))
    : null;
  const promoAvailable = promoRemaining !== null;
  const promoExpired = false;

  const effectiveMonthly = billingCycle === "yearly"
    ? (isIndia ? "₹33" : "$1.33")
    : (isIndia ? `₹${MONTHLY_INR}` : "$2");

  // Single fetch: /billing/config already returns subscription for PRO users
  const fetchBillingConfig = async () => {
    try {
      const res = await api.get("/billing/config");
      const data = res.data;
      setBillingConfig({
        isIndia: data.isIndia,
        preferredGateway: data.preferredGateway,
        currency: data.currency,
      });
      if (data.subscription) {
        setSubscription(data.subscription);
      }
    } catch (err) {
      console.error("Failed to fetch billing config:", err);
    }
  };

  const fetchPromoUsage = async () => {
    try {
      const res = await api.get(`/billing/promo/${PROMO_CODE}/remaining`);
      setPromoUsed(res.data.used ?? res.data.count ?? 0);
    } catch (err) {
      console.error("Failed to fetch promo usage:", err);
      setPromoUsed(0);
    }
  };

  useEffect(() => {
    fetchBillingConfig();
    fetchPromoUsage();
  }, []);

  useEffect(() => {
    if (window.Paddle) { initPaddle(); return; }
    const script = document.querySelector('script[src*="paddle.js"]');
    if (!script) return;
    script.addEventListener("load", initPaddle);
    return () => script.removeEventListener("load", initPaddle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => setRazorpayLoaded(true);
    document.body.appendChild(s);
  }, []);

  function initPaddle() {
    if (!window.Paddle) return;
    try {
      window.Paddle.Environment.set(process.env.NODE_ENV === "production" ? "production" : "sandbox");
      window.Paddle.Initialize({
        token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
        eventCallback(data) {
          if (data.name === "checkout.completed") { setStatus("success"); setTimeout(() => navigate("/pro-welcome"), 1500); }
          else if (data.name === "checkout.payment.failed") setStatus("failed");
        },
      });
      setPaddleLoaded(true);
    } catch (e) { setPaddleError(e.message); }
  }

  const handlePaddleCheckout = () => {
    if (!paddleLoaded) return;
    const priceId = billingCycle === "monthly"
      ? process.env.REACT_APP_PADDLE_PRICE_ID
      : process.env.REACT_APP_PADDLE_PRICE_ID_YEARLY;
    if (!priceId) { setPaddleError("Price ID missing in .env"); return; }
    setStatus(null);
    try {
      window.Paddle.Checkout.open({
        settings: { displayMode: "overlay", theme: "light", locale: "en" },
        items: [{ priceId, quantity: 1 }],
        customData: { userId: user?._id },
        ...(user?.email ? { customer: { email: user.email } } : {}),
      });
    } catch (e) { setPaddleError(e.message); }
  };

  const handleRazorpayCheckout = async () => {
    if (!razorpayLoaded || status === "processing") return;
    try {
      setStatus("processing");
      const res = await api.post("/billing/razorpay/subscribe", { planType: billingCycle, autoRenew: true });
      const data = res.data;
      if (!data.subscriptionId) throw new Error(data.message || "Subscription creation failed");

      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscriptionId,
        name: "Learnlia PRO",
        description: `${billingCycle === "monthly" ? "Monthly" : "Yearly"} PRO Access (Autopay)`,
        handler: async (response) => {
          try {
            const verify = await api.post("/billing/razorpay/verify", { ...response, planType: billingCycle });
            if (verify.data.success) { setStatus("success"); setTimeout(() => navigate("/pro-welcome"), 1500); }
            else setStatus("failed");
          } catch { setStatus("failed"); }
        },
        prefill: { name: user?.name, email: user?.email },
        notes: { userId: user?._id, planType: billingCycle },
        theme: { color: "#2563eb" },
      });
      rzp.on("payment.failed", () => setStatus("failed"));
      rzp.open();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
      setStatus("failed");
    }
  };

  const primaryCheckout = gateway === "razorpay" ? handleRazorpayCheckout : handlePaddleCheckout;
  const primaryReady = gateway === "razorpay" ? razorpayLoaded : paddleLoaded;

  // eslint-disable-next-line
  const handleCopyPromo = () => {
    navigator.clipboard.writeText(PROMO_CODE);
    setPromoCopied(true);
    setTimeout(() => setPromoCopied(false), 2000);
  };

  /* ── Active PRO view ── */
  if (isPro) {
    const formatDate = (d) =>
      new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

    return (
      <div className="up-page">
        <div className="up-hero">
          <div className="up-eyebrow up-eyebrow--active">Active plan</div>
          <h1>You're on Learnlia PRO</h1>
          <p>Full access to all tracks, AI assessments, projects, and verified certificates.</p>
        </div>

        <div className="up-plan up-plan--featured" style={{ maxWidth: 400, width: "100%" }}>
          <div className="up-featured-label">Active</div>
          <div className="up-plan-tier">PRO</div>

          {subscription ? (
            <div className="ms-plan-info">
              <p><strong>Status:</strong> {subscription.status}</p>
              <p><strong>Provider:</strong> {subscription.provider}</p>
              <p>
                <strong>Next billing date:</strong>{" "}
                {subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : "N/A"}
              </p>
            </div>
          ) : (
            <div className="ms-plan-info">
              <p style={{ opacity: 0.5 }}>Loading subscription details…</p>
            </div>
          )}

          <div className="up-divider" />
          <ul className="up-features">
            {FEATURES_PRO.map((f) => (
              <li key={f} className="up-feat">
                <span className="up-check up-check--yes">✓</span>{f}
              </li>
            ))}
          </ul>
          <button className="up-btn up-btn--primary" onClick={() => navigate("/tracks")}>
            Go to tracks →
          </button>
        </div>

        <div className="up-manage-footer">
          <p>Need to make changes to your billing?</p>
          <button className="up-manage-btn" onClick={() => setIsModalOpen(true)}>
            Manage Subscription
          </button>
        </div>

        <ManageSubscriptionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          subscription={subscription}
          onUpdate={fetchBillingConfig}
        />
      </div>
    );
  }

  /* ── Main upgrade page ── */
  return (
    <div className="up-page">
      <div className="up-hero">
        <div className="up-eyebrow">Learnlia PRO</div>
        <h1>Stop watching.<br />Start getting hired.</h1>
        <p>Most students complete 3 courses and stop. PRO gives you the structure, proof, and accountability to actually finish.</p>
      </div>

      {paddleError && <div className="up-banner up-banner--warn"><strong>Payment error:</strong> {paddleError}</div>}
      {status === "success" && <div className="up-banner up-banner--ok">Payment successful — activating your PRO access…</div>}
      {status === "failed" && <div className="up-banner up-banner--fail">Payment failed. Please try again or use a different method.</div>}

      <div className="up-urgency">
        <div className="up-urgency-dot" />
        <span>You've completed Phase 1. The next phase — and AI assessments — are locked on your free plan.</span>
      </div>

      {promoAvailable && (
        <div className="up-promo-banner">
          <div className="up-promo-banner__left">
            <div className="up-promo-banner__top">
              <span className="up-promo-banner__tag">Limited Deal</span>
              <span className="up-promo-banner__slots">
                <span className="up-promo-banner__slots-dot" />
                {promoRemaining} of {PROMO_TOTAL_SLOTS} spots left
              </span>
            </div>
            <p className="up-promo-banner__title">Get Learnlia PRO just for ₹49</p>
            <p className="up-promo-banner__sub">
              Access all premium features, tracks, and AI assessments for less than the cost of a coffee.
            </p>
          </div>
          <div className="up-promo-banner__right">
            {/* Promo logic preserved but UI simplified for the "Limited Deal" message */}
            <div className="up-promo-banner__highlight">
              🔥 Limited Time
            </div>
          </div>
        </div>
      )}

      {promoExpired && (
        <div className="up-banner up-banner--neutral">
          All {PROMO_TOTAL_SLOTS} founding member spots have been claimed. Standard pricing applies.
        </div>
      )}

      <div className="up-toggle-row">
        <span className={`up-toggle-label ${billingCycle === "monthly" ? "active" : ""}`} onClick={() => setBillingCycle("monthly")}>Monthly</span>
        <div className={`up-toggle-switch ${billingCycle === "yearly" ? "on" : ""}`} onClick={() => setBillingCycle(c => c === "monthly" ? "yearly" : "monthly")}>
          <div className="up-toggle-dot" />
        </div>
        <span className={`up-toggle-label ${billingCycle === "yearly" ? "active" : ""}`} onClick={() => setBillingCycle("yearly")}>
          Yearly <span className="up-save-pill">Save {SAVE_PCT}%</span>
        </span>
      </div>

      <div className="up-plans">
        <div className="up-plan">
          <div className="up-plan-tier">Free</div>
          <div className="up-plan-name">Starter</div>
          <div className="up-plan-price">{displayFree} <span>/ forever</span></div>
          <div className="up-plan-billing">No card required</div>
          <div className="up-divider" />
          <ul className="up-features">
            {FEATURES_FREE.map((f, i) => (
              <li key={i} className={`up-feat ${f.yes ? "" : "up-feat--dim"}`}>
                <span className={`up-check ${f.yes ? "up-check--yes" : "up-check--no"}`}>{f.yes ? "✓" : "✗"}</span>
                {f.text}
              </li>
            ))}
          </ul>
          <button className="up-btn up-btn--ghost" disabled>Current plan</button>
        </div>

        <div className="up-plan up-plan--featured">
          <div className="up-featured-label">Most popular</div>
          <div className="up-plan-tier">PRO</div>
          <div className="up-plan-name">Pro Track Access</div>

          {displayStrike && <div className="up-plan-strike">{displayStrike}</div>}
          <div className="up-plan-price">{displayPrice} <span>{displayPer}</span></div>
          {displayEffPer && <div className="up-plan-effective">{displayEffPer}</div>}
          <div className="up-plan-billing">{displayNote}</div>

          <div className="up-divider" />
          <ul className="up-features">
            {FEATURES_PRO.map((f, i) => (
              <li key={i} className="up-feat">
                <span className="up-check up-check--yes">✓</span>{f}
              </li>
            ))}
          </ul>

          {gateway === "razorpay" && (
            <div className="up-auto-renew" style={{ cursor: "default" }}>
              <span style={{ fontSize: "16px", color: "#16a34a", fontWeight: "bold" }}>✓</span>
              <div className="up-auto-renew-text">
                <span className="up-auto-renew-title">Auto-renewal (Autopay) enabled</span>
                <span className="up-auto-renew-sub">Cancel anytime in profile settings</span>
              </div>
            </div>
          )}

          <button
            className={`up-btn ${billingCycle === "yearly" ? "up-btn--purple" : "up-btn--primary"}`}
            onClick={primaryCheckout}
            disabled={!primaryReady || status === "processing"}
          >
            {status === "processing" ? "Processing…"
              : primaryReady ? `Get PRO — ${displayPrice}/${billingCycle === "monthly" ? "mo" : "yr"} →`
                : "Loading checkout…"}
          </button>

          {gateway === "razorpay" && (
            <button className="up-btn-alt" onClick={handlePaddleCheckout} disabled={!paddleLoaded}>
              Pay with card / PayPal (Paddle) →
            </button>
          )}

          {process.env.NODE_ENV === "development" && (
            <div className="up-debug">
              Gateway: {gateway} · Rzp: {razorpayLoaded ? "✓" : "…"} · Paddle: {paddleLoaded ? "✓" : "…"} · India: {String(isIndia)}
            </div>
          )}
        </div>
      </div>

      <div className="up-terms">
        {promoAvailable ? (
          <>
            <strong>Limited deal terms:</strong> This price is part of our founding member program.
            {billingCycle === "monthly"
              ? ` Pay only ₹49 today for full monthly access.`
              : ` Pay only ₹399 today for full annual access (saves ₹189).`}
            {" "}Cancel anytime. 7-day full refund — no questions asked.
            Limited to first {PROMO_TOTAL_SLOTS} users — {promoRemaining} spots remaining.
          </>
        ) : (
          <>
            {isIndia ? "₹49/month" : "$2/month"} billed monthly or {isIndia ? "₹399/year" : "$16/year"} billed annually.
            Cancel anytime. 7-day full refund — no questions asked.
          </>
        )}
      </div>

      {!isIndia && (
        <div className="up-global-section">
          <button className="up-global-toggle" onClick={() => setShowGlobal(g => !g)}>
            {showGlobal ? "Hide" : "See"} pricing for your country {showGlobal ? "↑" : "↓"}
          </button>
          {showGlobal && (
            <div className="up-global-table-wrap">
              <table className="up-global-table">
                <thead><tr><th>Country</th><th>Monthly</th><th>Yearly</th></tr></thead>
                <tbody>
                  {GLOBAL_PRICES.map(r => (
                    <tr key={r.country}>
                      <td><span className="up-flag">{r.flag}</span>{r.country}<span className="up-currency"> {r.currency}</span></td>
                      <td>{r.monthly}/mo</td>
                      <td>{r.yearly}/yr</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="up-global-note">Prices are purchasing-power adjusted. Paddle charges in your local currency at checkout.</p>
            </div>
          )}
        </div>
      )}

      <div className="up-proof-row">
        <div className="up-proof-card"><div className="up-proof-num">3x</div><div className="up-proof-label">faster completion vs free</div></div>
        <div className="up-proof-card"><div className="up-proof-num">{effectiveMonthly}</div><div className="up-proof-label">{billingCycle === "yearly" ? "effective per month on yearly" : "per month · cancel anytime"}</div></div>
        <div className="up-proof-card"><div className="up-proof-num">7 days</div><div className="up-proof-label">full refund if unsatisfied</div></div>
      </div>

      <div className="up-testimonials">
        {TESTIMONIALS.map((t, i) => (
          <div key={i} className="up-testimonial">
            <p className="up-t-text">"{t.text}"</p>
            <div className="up-t-author">
              <div className={`up-t-avatar up-t-avatar--${t.color}`}>{t.initials}</div>
              <span>{t.meta}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="up-trust">
        {[
          "Cancel anytime",
          `Secure via ${gatewayName}`,
          "Instant access on payment",
          "7-day full refund",
          isIndia ? "UPI · cards · net banking" : "Cards · PayPal · local methods",
        ].map((t, i) => (
          <div key={i} className="up-trust-item"><div className="up-trust-dot" />{t}</div>
        ))}
      </div>
    </div>
  );
}